import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHandsHelping, FaGraduationCap, FaUsers, FaHeart, FaArrowRight, FaEye, FaBullseye, FaStethoscope, FaBriefcase, FaTools, FaHeadphones } from 'react-icons/fa';


const Home = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, []);

  const services = [
    {
      icon: <FaEye className="text-4xl text-secondary" />,
      title: 'Rural Eye Care Outreach',
      description: 'Conducted thousands of eye-screening camps across villages in South Tamil Nadu.',
    },
    {
      icon: <FaStethoscope className="text-4xl text-primary" />,
      title: 'Surgical Intervention & Treatment',
      description: 'Provided surgeries and treatments for those identified with visual impairments.',
    },
    {
      icon: <FaHeart className="text-4xl text-accent" />,
      title: 'Affordable Hospital Services',
      description: 'Operated a 150-bed eye hospital established in 1990 (active till 2015), providing affordable care to thousands.',
    },
    {
      icon: <FaUsers className="text-4xl text-secondary" />,
      title: 'Independent Living Training',
      description: 'Trained visually impaired individuals in daily living skills for independent living.',
    },
    {
      icon: <FaBriefcase className="text-4xl text-primary" />,
      title: 'Livelihood Empowerment',
      description: 'Supported over 70 blind individuals with livelihood support such as small shops, STD booths, chair caning, and animal husbandry.',
    },
    {
      icon: <FaArrowRight className="text-4xl text-accent" />,
      title: 'Regional Service Expansion',
      description: 'Expanded services from Madurai to Virudhunagar, Sivagangai and Ramanathapuram.',
    },
    {
      icon: <FaTools className="text-4xl text-secondary" />,
      title: 'Vocational Skills Development',
      description: 'Empowered people with multiple disabilities through training in envelope making, notebook production, and other vocational skills supplying materials to LIC across India.',
    },
    {
      icon: <FaHeadphones className="text-4xl text-primary" />,
      title: 'Educational Audio Resources',
      description: 'Released audiobooks in 2007, enabling countless visually impaired students to become doctors, professors, and successful professionals.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary via-secondary to-accent text-white py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1756454487569-903cdde4b92a)',
            opacity: 0.3
          }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="text-center">
            <h1 
              className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in"
              data-testid="hero-title"
            >
              "Prevention • Cure • Rehabilitation" Empowering Lives Since 1979 
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto opacity-95">
              Join us in creating an inclusive world where every individual has the opportunity to shine and thrive
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services"
                data-testid="explore-services-btn"
                className="bg-white text-primary px-8 py-4 rounded-full font-semibold hover:bg-warm transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center space-x-2"
              >
                <span>Explore Our Services</span>
                <FaArrowRight />
              </Link>
              <Link
                to="/donate"
                data-testid="donate-now-btn"
                className="relative bg-gradient-to-r from-red-500 via-pink-500 to-red-600 text-white px-12 py-5 rounded-full font-bold text-lg hover:from-red-600 hover:to-pink-600 transition-all duration-300 transform hover:scale-110 shadow-2xl flex items-center justify-center space-x-3 animate-pulse"
              >
                <FaHeart className="text-2xl animate-bounce" />
                <span>Donate Now</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-16 bg-warm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-lg" data-testid="mission-card">
              <div className="flex items-center mb-4">
                <div className="bg-secondary p-3 rounded-full mr-4">
                  <FaBullseye className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Our Mission</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                To identify, support, empower and integrate individuals with disabilities into mainstream society through comprehensive services focused on Prevention, Cure and Rehabilitation. 
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg" data-testid="vision-card">
              <div className="flex items-center mb-4">
                <div className="bg-primary p-3 rounded-full mr-4">
                  <FaEye className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Our Vision</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                A society where every person with a disability lives with dignity, independence, and equal opportunities. 
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Highlights */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" data-testid="services-highlight-title">
              How We Make a Difference
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Our comprehensive programs are designed to support, educate, and empower
            </p>
          </div>

          {/* First 6 cards in 3-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service, index) => (
              <div
                key={index}
                data-testid={`service-card-${index}`}
                className="bg-white border-2 border-gray-100 rounded-xl p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="flex justify-center mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
          
          {/* Last 2 cards centered in 2-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mt-8">
            {services.slice(6).map((service, index) => (
              <div
                key={index + 6}
                data-testid={`service-card-${index + 6}`}
                className="bg-white border-2 border-gray-100 rounded-xl p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="flex justify-center mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Statistics */}
      <section className="py-16 bg-gradient-to-r from-primary to-secondary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div data-testid="stat-beneficiaries">
              <h3 className="text-4xl md:text-5xl font-bold mb-2">5000+</h3>
              <p className="text-lg opacity-90">Beneficiaries</p>
            </div>
            <div data-testid="stat-programs">
              <h3 className="text-4xl md:text-5xl font-bold mb-2">50+</h3>
              <p className="text-lg opacity-90">Programs</p>
            </div>
            <div data-testid="stat-volunteers">
              <h3 className="text-4xl md:text-5xl font-bold mb-2">200+</h3>
              <p className="text-lg opacity-90">Volunteers</p>
            </div>
            <div data-testid="stat-years">
              <h3 className="text-4xl md:text-5xl font-bold mb-2">45+</h3>
              <p className="text-lg opacity-90">Years</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-warm via-white to-warm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Be Part of Our Journey
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Every contribution helps us create more opportunities for individuals with disabilities. 
            Join us in making a lasting impact on lives.
          </p>
          <Link
            to="/donate"
            data-testid="cta-donate-btn"
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-red-500 via-pink-500 to-red-600 text-white px-12 py-5 rounded-full font-bold text-lg hover:from-red-600 hover:to-pink-600 shadow-2xl transition-all duration-300 transform hover:scale-110 border-4 border-white"
          >
            <FaHeart className="text-2xl" />
            <span>Support Our Cause - Donate Today</span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home